import React, { useMemo, useState, useCallback } from "react";
import AddFacilityGroupDialog from "./addFacilityGroupDialog";
import Accordion from "../../../../../components/accordion";
import { BtnComponent } from "../../../../../components/button";
import { InputField } from "../../../../../components/reactHookFormFields/inputField";
import { useForm } from "react-hook-form";
import { useGroupChatContext } from "../../../../../context/groupchatContext";
import ActionDialog from "../../../../../components/actionDialog";
import { toast } from "react-toastify";
import FacilityGroupListing from "./groupListing";
import {
    useDeleteFacilityLocationGroupMutation,
  useGetFacilityLocationGroupsQuery,
  useLazyGetGroupByIdQuery,
  useSaveFacilityLocationGroupMutation,
} from "../../../../../services/settingService";

function FacilityLocationGroup() {

    const groupChatContext = useGroupChatContext();
    const {authData} = useMemo(() => ({
        authData: groupChatContext.authData,
    }));

    // states
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [searchKeyword, setSearchKeyword] = useState("");
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, group: null });

    const [saveGroupTrigger, { isLoading: isSavingGroup }] = useSaveFacilityLocationGroupMutation();
    const [deleteGroupTrigger, { isLoading: deletingGroup }] = useDeleteFacilityLocationGroupMutation();
    const [getGroupById] = useLazyGetGroupByIdQuery();
    
    // form management
    const { control, reset, getValues } = useForm({
        defaultValues: {
            group_name: "",
        },
    });

    // triggers - API will be called with keyword param when searchKeyword changes
    const { data: groupsData, isLoading: groupsLoading, refetch: refetchGroups } = useGetFacilityLocationGroupsQuery(
        searchKeyword ? { keyword: searchKeyword } : {},
    );

    const handleSearch = () => {
        const formData = getValues();
        const keyword = formData?.group_name?.trim() || "";
        setSearchKeyword(keyword);
    };

    const handleReset = () => {
        reset({
            group_name: "",
        });
        setSearchKeyword("");
    };

    const handleEdit = async (group) => {
        try {
            const response = await getGroupById({ id: group?.id }).unwrap();
            const detail = response?.data || group;
            setSelectedGroup(detail);
            setShowAddDialog(true);
        } catch (error) {
            toast.error(error?.data?.msg || "Failed to fetch group detail");
        }
    };

    const handleAdd = () => {
        setSelectedGroup(null);
        setShowAddDialog(true);
    };

    const handleDeleteClick = useCallback((group) => {
        setDeleteConfirm({ open: true, group });
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        const group = deleteConfirm.group;
        if (!group?.id) return;
        try {
            const res = await deleteGroupTrigger({ id: group.id }).unwrap();
            if (res?.code == 0) {
                toast.success(res?.msg || "Group deleted successfully");
                refetchGroups();
            } else {
                toast.error(res?.msg || "Failed to delete group");
            }
        } catch (err) {
            toast.error(err?.data?.msg || "Failed to delete group");
        } finally {
            setDeleteConfirm({ open: false, group: null });
        }
    }, [deleteConfirm.group, deleteGroupTrigger, refetchGroups]);

    const handleDeleteCancel = useCallback(() => {
        setDeleteConfirm({ open: false, group: null });
    }, []);

    const handleSubmitGroup = useCallback(async (payload) => {
        try {
            const res = await saveGroupTrigger(payload).unwrap();
            if (res?.code == 0) {
                toast.success(res?.msg || (payload?.id ? "Group updated successfully" : "Group added successfully"));
                refetchGroups();
                return true;
            }
            toast.error(res?.msg || "Failed to save group");
            return false;
        } catch (error) {
            toast.error(error?.data?.msg || "Failed to save group");
            return false;
        }
    }, [saveGroupTrigger, refetchGroups]);

    return (
        <div>
            {showAddDialog ? (
                <AddFacilityGroupDialog
                    setShowAddDialog={setShowAddDialog}
                    selectedGroup={selectedGroup}
                    setSelectedGroup={setSelectedGroup}
                    onSubmitGroup={handleSubmitGroup}
                    isSavingGroup={isSavingGroup}
                />
            ) : null}

            <div className="py-2 pl-3 pr-[0.8rem]">
                <Accordion
                    title="Group Search"
                    open={false}
                    showDialogue={true}
                    rightDialogHandler={handleAdd}
                    size="sm"
                    icon={
                        <BtnComponent
                            text="Add"
                            btnType={authData?.is_provider ? 'primary' : 'primary_theme'}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleAdd();
                            }}
                        />
                    }
                    iconTooltip="Add Facility Group"
                >
                <div className="pt-4">
                    <div className="row flex gap-x-4 pb-3 w-full">
                        <div className="w-1/2 md:w-1/4">
                            <InputField
                                title="Group Name"
                                name="group_name"
                                control={control}
                                placeholder=""
                            />
                        </div>
                        <div className="w-1/4 flex items-end">
                            <BtnComponent 
                                text="Search" 
                                btnType="primary" 
                                onClick={handleSearch}
                            />
                            <BtnComponent 
                                className="ml-5"
                                text="Reset" 
                                btnType="delete" 
                                onClick={handleReset}
                            />
                        </div>
                    </div>
                </div>
                </Accordion>
            </div>
            <FacilityGroupListing
                groups={groupsData?.data}
                groupsLoading={groupsLoading || false}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
                deletingGroup={deletingGroup}
            />

            <ActionDialog
                showModal={deleteConfirm.open}
                setShowModal={handleDeleteCancel}
                title="Are you sure you want to delete this group?"
                onCancelHandler={handleDeleteCancel}
                onConfirmHandler={handleDeleteConfirm}
                confirmButtonText="Yes"
                cancelButtonText="No"
            >
            </ActionDialog>
        </div>
    );
}

export default FacilityLocationGroup;
